/**
 * Formatting utilities for chart labels and tooltips
 */

/**
 * Format Unix timestamp to time string
 * @param timestamp Unix timestamp in milliseconds
 * @param format 'short' | 'medium' | 'long'
 * @returns Formatted time string
 */
export const formatTime = (timestamp: number, format: 'short' | 'medium' | 'long' = 'short'): string => {
    const date = new Date(timestamp);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();

    const pad = (num: number) => String(num).padStart(2, '0');
    const hour12 = hours % 12 || 12;
    const ampm = hours >= 12 ? 'PM' : 'AM';

    switch (format) {
        case 'short':
            return `${pad(hour12)}:${pad(minutes)}`;
        case 'medium':
            return `${pad(hour12)}:${pad(minutes)} ${ampm}`;
        case 'long':
            return `${pad(hour12)}:${pad(minutes)}:${pad(seconds)} ${ampm}`;
        default:
            return `${pad(hour12)}:${pad(minutes)}`;
    }
};

/**
 * Format Unix timestamp to date string
 * @param timestamp Unix timestamp in milliseconds
 * @param format 'short' | 'medium' | 'long'
 * @returns Formatted date string
 */
export const formatDate = (timestamp: number, format: 'short' | 'medium' | 'long' = 'medium'): string => {
    const date = new Date(timestamp);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear();

    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthName = monthNames[date.getMonth()];

    switch (format) {
        case 'short':
            return `${month}/${day}`;
        case 'medium':
            return `${monthName} ${day}`;
        case 'long':
            return `${monthName} ${day}, ${year}`;
        default:
            return `${monthName} ${day}`;
    }
};

/**
 * Format price/odds value
 * @param value Numeric value
 * @param decimals Number of decimal places
 * @returns Formatted price string
 */
export const formatPrice = (value: number, decimals: number = 2): string => {
    return value.toFixed(decimals);
};

/**
 * Format currency value
 * @param value Numeric value
 * @param currency Currency symbol (default: $)
 * @returns Formatted currency string
 */
export const formatCurrency = (value: number, currency: string = '$'): string => {
    const absValue = Math.abs(value);
    const sign = value < 0 ? '-' : '';

    if (absValue >= 1000000) {
        return `${sign}${currency}${(absValue / 1000000).toFixed(2)}M`;
    }
    if (absValue >= 1000) {
        return `${sign}${currency}${(absValue / 1000).toFixed(2)}K`;
    }
    return `${sign}${currency}${absValue.toFixed(2)}`;
};

/**
 * Format percentage value
 * @param value Numeric value (e.g., 0.45 or 45)
 * @param asDecimal Whether value is in decimal form (0.45) vs percentage (45)
 * @returns Formatted percentage string
 */
export const formatPercentage = (value: number, asDecimal: boolean = false): string => {
    const percent = asDecimal ? value * 100 : value;
    return `${percent.toFixed(2)}%`;
};

/**
 * Format ROI value with + or - prefix
 * @param value ROI value
 * @returns Formatted ROI string
 */
export const formatROI = (value: number): string => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
};

/**
 * Smart time label based on data range
 * Automatically chooses appropriate format based on time span
 */
export const getSmartTimeLabel = (timestamp: number, minTime: number, maxTime: number): string => {
    const range = maxTime - minTime;
    const oneHour = 60 * 60 * 1000;
    const oneDay = 24 * oneHour;
    const oneWeek = 7 * oneDay;

    if (range < oneHour) {
        // Less than 1 hour: show time only
        return formatTime(timestamp, 'short');
    } else if (range < oneDay) {
        // Less than 1 day: show time with AM/PM
        return formatTime(timestamp, 'medium');
    } else if (range < oneWeek) {
        // Less than 1 week: show date + time
        return `${formatDate(timestamp, 'short')} ${formatTime(timestamp, 'short')}`;
    } else {
        // More than 1 week: show date only
        return formatDate(timestamp, 'medium');
    }
};

/**
 * Format large numbers with K/M suffix
 */
export const formatLargeNumber = (value: number, decimals: number = 1): string => {
    const absValue = Math.abs(value);
    const sign = value < 0 ? '-' : '';

    if (absValue >= 1000000) {
        return `${sign}${(absValue / 1000000).toFixed(decimals)}M`;
    }
    if (absValue >= 1000) {
        return `${sign}${(absValue / 1000).toFixed(decimals)}K`;
    }
    return `${sign}${absValue.toFixed(decimals)}`;
};

/**
 * Calculate nice Y-axis bounds
 * Finds clean min/max values for Y-axis
 */
export const calculateNiceYBounds = (values: number[]): { min: number; max: number } => {
    if (values.length === 0) return { min: 0, max: 1 };

    const dataMin = Math.min(...values);
    const dataMax = Math.max(...values);
    const range = dataMax - dataMin;

    // Add 10% padding
    const padding = range * 0.1;
    const min = Math.floor((dataMin - padding) * 100) / 100;
    const max = Math.ceil((dataMax + padding) * 100) / 100;

    return { min: Math.max(0, min), max };
};

/**
 * Generate evenly spaced Y-axis labels
 */
export const generateYAxisLabels = (min: number, max: number, count: number = 6): number[] => {
    const labels: number[] = [];
    const step = (max - min) / (count - 1);

    for (let i = 0; i < count; i++) {
        labels.push(min + (step * i));
    }

    return labels;
};
